import axios, { AxiosError } from 'axios';
import { BASE_URL, SERIES_API_PATH } from '../../../constants/api';
import { ApiService } from '../../../shared/service/api/apiService';
import { I18nApiErrorHandler } from '../../../shared/services/i18n/i18nApiErrorHandler';
import { FetchResult } from '../../../shared/type/apiType';
import { PosterInterface } from '../../../shared/type/seasonType';
import {
  ElevenDbSeriesInterface,
  SeriesApiRequest,
  SeriesData,
  SeriesInterface,
  SeriesListItem,
} from '../type/seriesType';

class SeriesService {
  constructor(private apiService: ApiService<SeriesInterface>) {}

  public async fetch(params?: URLSearchParams): Promise<FetchResult<SeriesInterface>> {
    const response = await this.apiService.fetch(params);
    return response;
  }

  public async fetchById(seriesId: number): Promise<SeriesInterface> {
    if (!seriesId) {
      return {} as SeriesInterface;
    }
    const response = await this.apiService.fetchById(seriesId);
    return response;
  }

  public async fetchByIds(
    seriesIds: number[],
    query?: Record<string, string>
  ): Promise<FetchResult<SeriesInterface>> {
    if (seriesIds.length === 0) {
      return {
        results: [],
        count: 0,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: 0,
      };
    }
    return await this.apiService.fetchByIds(seriesIds, query);
  }

  public async create(series: SeriesInterface): Promise<SeriesInterface> {
    return await this.apiService.create(series);
  }

  public async update(seriesId: number, series: Partial<SeriesInterface>): Promise<void> {
    await this.apiService.update(seriesId, series);
  }

  public async delete(seriesId: number): Promise<void> {
    await this.apiService.delete(seriesId);
  }

  /**
   * Fetch Series list with minimal fields for list page
   * Uses SeriesListSerializer
   */
  public async fetchSeriesList(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeriesListItem>> {
    try {
      const params = new URLSearchParams(queryParams);

      // Uses SeriesListSerializer (automatically applied by backend)
      // Serializer fields: id, title, title_en, slug, status, release_date_start, release_date_end, posters, age_rating, dubbing_mode, created, modified
      params.set('serializer', 'list');

      const response = await axios.get(`${BASE_URL}${SERIES_API_PATH}`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Fetch Series list with i18n fields (all fields)
   */
  public async fetchSeriesListWithI18n(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeriesData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // Set field list including i18n fields
      const i18nFields = [
        'id',
        'slug',
        'status',
        'created',
        'modified',
        'dubbing_mode',
        'release_date_start',
        'release_date_end',
        'categories',
        'cast',
        'age_rating',
        'produce_country',
        'seasons',
        'is_deletable',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
      ].join(',');
      params.set('fields', i18nFields);

      const response = await axios.get(`${BASE_URL}${SERIES_API_PATH}`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Fetch single Series with i18n fields
   */
  public async fetchSeriesWithI18n(id: number): Promise<SeriesData> {
    try {
      const fields = [
        'id',
        'slug',
        'status',
        'created',
        'modified',
        'dubbing_mode',
        'release_date_start',
        'release_date_end',
        'categories',
        'cast',
        'age_rating',
        'produce_country',
        'seasons',
        'is_deletable',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
      ].join(',');

      const params = { fields };
      const response = await axios.get(`${BASE_URL}${SERIES_API_PATH}${id}/`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Create Series with both legacy and i18n fields
   */
  public async createSeriesWithI18n(seriesData: SeriesApiRequest): Promise<SeriesData> {
    try {
      const response = await axios.post(`${BASE_URL}${SERIES_API_PATH}`, seriesData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Update Series with both legacy and i18n fields
   */
  public async updateSeriesWithI18n(
    id: number,
    seriesData: Partial<SeriesApiRequest>
  ): Promise<SeriesData> {
    try {
      const response = await axios.patch(`${BASE_URL}${SERIES_API_PATH}${id}/`, seriesData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Fetch Series list for a specific language (filter by language)
   */
  public async fetchSeriesByLanguage(
    language: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeriesData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // Filter only Series with translations in the specific language
      params.set('has_translation', language);

      return this.fetchSeriesListWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * Fetch Series by translation completeness
   */
  public async fetchSeriesByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeriesData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // Required language translation status filter
      params.set('required_languages', requiredLanguages.join(','));

      return this.fetchSeriesListWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  public async fetchPoster(seriesId: number): Promise<PosterInterface[]> {
    const response = await axios.get(`${BASE_URL}${SERIES_API_PATH}${seriesId}/poster/`);
    return response.data;
  }

  public async uploadPoster(seriesId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        const formData = new FormData();
        formData.append('file', poster.originFileObj!);
        formData.append('is_representative', poster.is_representative.toString());
        if (poster.language) {
          formData.append('language', poster.language);
        }
        if (poster.orientation) {
          formData.append('orientation', poster.orientation);
        }
        const response = await axios.post(
          `${BASE_URL}${SERIES_API_PATH}${seriesId}/poster/`,
          formData
        );
        return response.data;
      })
    );
  }

  public async removePoster(seriesId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        await axios.delete(`${BASE_URL}${SERIES_API_PATH}${seriesId}/poster/${poster.id}/`);
      })
    );
  }

  public async updatePoster(seriesId: number, poster: PosterInterface): Promise<void> {
    await axios.patch(`${BASE_URL}${SERIES_API_PATH}${seriesId}/poster/${poster.id}/`, poster);
  }

  public async fetchElevenDbSeries(seriesId: number): Promise<ElevenDbSeriesInterface> {
    return (await this.apiService.fetchChildItemsByParentId<ElevenDbSeriesInterface>(
      seriesId,
      'eleven-db/'
    )) as ElevenDbSeriesInterface;
  }

  public async updateElevenDbSeries(
    seriesId: number,
    elevenDbSeries: Partial<ElevenDbSeriesInterface>
  ): Promise<void> {
    await this.apiService.updateItemsForParent(seriesId, 'eleven-db/', elevenDbSeries);
  }

  public async createElevenDbSeries(
    seriesId: number,
    elevenDbSeries: ElevenDbSeriesInterface
  ): Promise<void> {
    await this.apiService.createItemsForParent(seriesId, 'eleven-db/', elevenDbSeries);
  }
}

export const seriesService = new SeriesService(new ApiService<SeriesInterface>(SERIES_API_PATH));
